import { useQuery } from "@tanstack/react-query";
import { versionsApi } from "./versions.api";
import { versionsKeys } from "./versions.keys";

export const useSearchVersionsQuery = (
  params: {
    search?: string;
    size?: number;
    page?: number;
    sort?: string;
    order?: string;
    userId?: string;
    pageIds?: string[];
    /**
     * The Date filter. These were missing here, so the list built them, the API
     * layer accepted them — and this hook dropped them in between: the filter
     * never reached the server and, because the query key did not carry them
     * either, picking a window refetched the same unfiltered rows.
     */
    dateField?: "createdAt" | "updatedAt";
    dateFrom?: string;
    dateTo?: string;
    /** The dashboard's "Recently viewed" scope — the caller's own opens. */
    recentlyViewed?: boolean;
    /** Comma-separated version statuses: Live, Expired, Draft, Accepted. */
    statuses?: string;
    /**
     * Leave the stories' own primary versions out of the result.
     *
     * The lists that show versions have always hidden them, but they did it to
     * the response — after the server had already cut a page of 25. A primary
     * version is touched on every save of its story, so org-wide the newest
     * versions are nearly all primary: the dashboard fetched 25, dropped 24 and
     * showed one row. The server owns the filter now, so a page is a page.
     */
    excludePrimary?: boolean;
  },
  options?: { enabled?: boolean },
) => {
  const queryParams = Object.entries({
    search: params.search,
    size: params.size,
    page: params.page,
    sort: params.sort,
    order: params.order,
    userId: params.userId,
    pageIds: params.pageIds,
    dateField: params.dateField,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    recentlyViewed: params.recentlyViewed,
    statuses: params.statuses,
    excludePrimary: params.excludePrimary,
  }).reduce<Record<string, string | number | boolean | string[]>>(
    (acc, [key, value]) => {
      if (value !== undefined && value !== "") {
        if (Array.isArray(value) && value.length === 0) {
          // Skip empty arrays
          return acc;
        }
        // pageIds will be converted to comma-separated string in the API layer
        acc[key] = value;
      }
      return acc;
    },
    {},
  );

  return useQuery({
    queryKey: [...versionsKeys.all, "search", queryParams],
    queryFn: () => {
      const apiParams: {
        search: string;
        size: number;
        page: number;
        sort?: string;
        order?: string;
        userId?: string;
        pageIds?: string[];
        dateField?: "createdAt" | "updatedAt";
        dateFrom?: string;
        dateTo?: string;
        recentlyViewed?: boolean;
        statuses?: string;
        excludePrimary?: boolean;
      } = {
        search: params.search ?? "",
        size: params.size ?? 25,
        page: params.page ?? 0,
      };
      if (params.sort) apiParams.sort = params.sort;
      if (params.order) apiParams.order = params.order;
      if (params.userId) apiParams.userId = params.userId;
      if (params.pageIds) apiParams.pageIds = params.pageIds;
      // Only alongside a bound: the field on its own narrows nothing and would
      // split the cache for no reason.
      if (params.dateFrom || params.dateTo) {
        if (params.dateField) apiParams.dateField = params.dateField;
        if (params.dateFrom) apiParams.dateFrom = params.dateFrom;
        if (params.dateTo) apiParams.dateTo = params.dateTo;
      }
      if (params.recentlyViewed) apiParams.recentlyViewed = true;
      if (params.statuses) apiParams.statuses = params.statuses;
      if (params.excludePrimary) apiParams.excludePrimary = true;
      return versionsApi.searchVersions(apiParams);
    },
    refetchOnMount: "always",
    enabled: options?.enabled !== false,
  });
};
